import { ExpenseListNavbarStore } from "@components/modules/expenses/listing/components/navbar/store";
import { Banner, DataTable } from "@components/ui-kit";
import { Pagination } from "@components/ui-kit/table/pagination";
import { Trans, t } from "@lingui/macro";
import type { ExpensesWhereColumn } from "@src/__generated__/graphql";
import { MarkAsUnpaidConfirm } from "@src/components/widgets/Modals/MarkAsUnpaidConfirm";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { type FC, useContext, useState } from "react";
import ExpenseListNavbar from "./components/navbar";
import { ExpenseItemsListing } from "./expense-items";
import { PageExpensesCtx } from "./PageExpenses";
import { TableExpenses } from "./table/TableExpenses";

export const CardListExpenses: FC<{ isSummaryLoaded: boolean }> = observer(
  function CardListExpenses({ isSummaryLoaded }) {
    const appStore = useStore();
    const store = useContext(PageExpensesCtx);
    const [navbarStore] = useState(
      () => new ExpenseListNavbarStore(appStore, store),
    );

    return (
      <div data-testid="card-list-projects">
        {navbarStore.warn && (
          <div className="mb-10">
            <Banner title={t`Please select expenses first.`} status="error" />
          </div>
        )}

        {navbarStore.duplicatePayment && (
          <div className="mb-10">
            <Banner
              title={t`Payment order cannot be generated for draft or already paid expenses`}
              status="info"
              primaryAction={{
                title: t`Unselect expenses`,
                onClick: navbarStore.onAction,
              }}
            />
          </div>
        )}

        <DataTable>
          <DataTable.NavBar>
            <ExpenseListNavbar
              expensesStore={store}
              navbarStore={navbarStore}
            />
          </DataTable.NavBar>

          {store.isExpenseItemsView ? (
            <ExpenseItemsListing />
          ) : (
            <>
              <DataTable.Filter>
                <FiltersPanel<ExpensesWhereColumn>
                  filters={store.where}
                  searchDefaultValue={store.searchTerm}
                  searchPlaceholder={t`Search by type, contractor, project/s, var. symbol, IBAN`}
                  onSearch={(val) => {
                    store.searchTerm = val;
                    store.pagination.resetPage();
                  }}
                />
              </DataTable.Filter>

              <BoxSummary isLoaded={isSummaryLoaded} />

              <TableExpenses />

              <Pagination store={store.pagination} />
            </>
          )}
        </DataTable>
        <MarkAsUnpaidConfirm
          isOpen={store.markAsUnpaidModal.isOpen}
          onClose={store.markAsUnpaidModal.onClose}
          hasMatchedPayments={
            store.markAsUnpaidModal.additionalData?.hasMatchedPayments ?? false
          }
          isLoading={store.isMarkingAsUnpaid}
          onConfirm={store.markAsUnpaid}
        />
      </div>
    );
  },
);

const BoxSummary: FC<{ isLoaded: boolean }> = observer(function BoxSummary({
  isLoaded,
}) {
  const store = useContext(PageExpensesCtx);

  const {
    expenses,
    overdue_by_31_to_90_days,
    overdue_by_31_to_90_days_vat,
    overdue_by_31_to_90_days_without_vat,
    overdue_by_91_to_365_days,
    overdue_by_91_to_365_days_vat,
    overdue_by_91_to_365_days_without_vat,
    overdue_by_less_than_30_days,
    overdue_by_less_than_30_days_vat,
    overdue_by_less_than_30_days_without_vat,
    overdue_by_more_than_365_days,
    overdue_by_more_than_365_days_vat,
    overdue_by_more_than_365_days_without_vat,
    payments_due,
    payments_due_vat,
    payments_due_without_vat,
    total_amount,
    total_vat,
    total_amount_with_vat,
  } = store.expensesSummary ?? {
    expenses: 0,
    overdue_by_31_to_90_days: 0,
    overdue_by_31_to_90_days_vat: 0,
    overdue_by_31_to_90_days_without_vat: 0,
    overdue_by_91_to_365_days: 0,
    overdue_by_91_to_365_days_vat: 0,
    overdue_by_91_to_365_days_without_vat: 0,
    overdue_by_less_than_30_days: 0,
    overdue_by_less_than_30_days_vat: 0,
    overdue_by_less_than_30_days_without_vat: 0,
    overdue_by_more_than_365_days: 0,
    overdue_by_more_than_365_days_vat: 0,
    overdue_by_more_than_365_days_without_vat: 0,
    payments_due: 0,
    payments_due_vat: 0,
    payments_due_without_vat: 0,
    total_amount: 0,
    total_vat: 0,
    total_amount_with_vat: 0,
  };

  const overdue_by_91_days_without_vat = computed(() => {
    return (
      overdue_by_91_to_365_days_without_vat +
      overdue_by_more_than_365_days_without_vat
    );
  }).get();

  const overdue_by_91_days_vat = computed(() => {
    return overdue_by_91_to_365_days_vat + overdue_by_more_than_365_days_vat;
  }).get();

  const overdue_by_91_days = computed(() => {
    return overdue_by_91_to_365_days + overdue_by_more_than_365_days;
  }).get();

  return (
    <DataTable.SummaryBox justify="space-between" hideBg>
      <DataTable.SummaryItem label={t`Expenses`} isLoaded={isLoaded}>
        {expenses}
      </DataTable.SummaryItem>
      <DataTable.SummaryItem
        label={t`Amount total`}
        isLoaded={isLoaded}
        alignment="right"
      >
        {currency.formatByWorkspace(total_amount, {
          czkNoFractionDigits: true,
        })}
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(total_vat, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(VAT)</Trans>
        </DataTable.SummaryItemSubText>
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(total_amount_with_vat, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(with VAT)</Trans>
        </DataTable.SummaryItemSubText>
      </DataTable.SummaryItem>
      <DataTable.SummaryItem
        label={t`Current`}
        isLoaded={isLoaded}
        alignment="right"
      >
        {currency.formatByWorkspace(payments_due_without_vat, {
          czkNoFractionDigits: true,
        })}
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(payments_due_vat, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(VAT)</Trans>
        </DataTable.SummaryItemSubText>
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(payments_due, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(with VAT)</Trans>
        </DataTable.SummaryItemSubText>
      </DataTable.SummaryItem>
      <DataTable.SummaryItem
        alignment="right"
        label={t`Overdue 0-30 days`}
        isLoaded={isLoaded}
      >
        {currency.formatByWorkspace(overdue_by_less_than_30_days_without_vat, {
          czkNoFractionDigits: true,
        })}
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(overdue_by_less_than_30_days_vat, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(VAT)</Trans>
        </DataTable.SummaryItemSubText>
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(overdue_by_less_than_30_days, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(with VAT)</Trans>
        </DataTable.SummaryItemSubText>
      </DataTable.SummaryItem>
      <DataTable.SummaryItem
        alignment="right"
        label={t`Overdue by 31-90 days`}
        isLoaded={isLoaded}
      >
        {currency.formatByWorkspace(overdue_by_31_to_90_days_without_vat, {
          czkNoFractionDigits: true,
        })}
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(overdue_by_31_to_90_days_vat, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(VAT)</Trans>
        </DataTable.SummaryItemSubText>
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(overdue_by_31_to_90_days, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(with VAT)</Trans>
        </DataTable.SummaryItemSubText>
      </DataTable.SummaryItem>
      <DataTable.SummaryItem
        alignment="right"
        label={t`Overdue by 91+ days`}
        isLoaded={isLoaded}
      >
        {currency.formatByWorkspace(
          overdue_by_91_days_without_vat,

          {
            czkNoFractionDigits: true,
          },
        )}
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(overdue_by_91_days_vat, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(VAT)</Trans>
        </DataTable.SummaryItemSubText>
        <DataTable.SummaryItemSubText>
          {currency.formatByWorkspace(overdue_by_91_days, {
            czkNoFractionDigits: true,
          })}
          &nbsp;<Trans>(with VAT)</Trans>
        </DataTable.SummaryItemSubText>
      </DataTable.SummaryItem>
    </DataTable.SummaryBox>
  );
});
